import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Copy, Edit, Trash2, Clock, Share2, Download, Eye, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import ShareModal from '../components/ShareModal';
import ShareManagement from '../components/ShareManagement';
import './KubeconfigDetail.css';

interface Vpn {
  id: string;
  name: string;
  description?: string;
  serverAddress?: string;
  protocol?: string;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  data?: {
    content: string;
  };
}

const VpnDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareManagement, setShowShareManagement] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVpn, setEditingVpn] = useState<Vpn | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    tags: [] as string[],
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { data, isLoading } = useQuery(
    ['vpn', id],
    async () => {
      const response = await api.get(`/vpn/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/vpn/${id}`);
    },
    {
      onSuccess: () => {
        toast.success('Vpn deleted successfully');
        navigate('/vpn');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to delete vpn');
      },
    }
  );

  const vpn: Vpn = data;

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = async () => {
    if (!vpn?.data?.content) return;
    
    try {
      const blob = new Blob([vpn.data.content], { type: 'application/x-ovpn' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${vpn.name.replace(/[^a-z0-9]/gi, '_')}_vpn_vpn.ovpn`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Vpn downloaded successfully');
    } catch (error) {
      toast.error('Failed to download vpn');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this vpn?')) {
      deleteMutation.mutate();
    }
  };

  const handleEdit = () => {
    if (!vpn) return;
    setEditingVpn(vpn);
    setFormData({
      name: vpn.name,
      description: vpn.description || '',
      content: '',
      tags: vpn.tags || [],
      notes: vpn.notes || '',
    });
    setUploadFile(null);
    setShowEditModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.tags.length > 0) formDataToSend.append('tags', JSON.stringify(formData.tags));
      if (formData.notes) formDataToSend.append('notes', formData.notes);

      if (uploadFile) {
        formDataToSend.append('file', uploadFile);
      } else if (formData.content) {
        formDataToSend.append('content', formData.content);
      }

      const formDataObj: any = {
        name: formData.name,
      };
      if (formData.description) formDataObj.description = formData.description;
      if (formData.notes) formDataObj.notes = formData.notes;
      if (formData.tags.length > 0) formDataObj.tags = formData.tags;
      if (formData.content) formDataObj.content = formData.content;

      // Use FormData for file upload, otherwise use JSON
      if (uploadFile) {
        const formDataToSend = new FormData();
        Object.keys(formDataObj).forEach(key => {
          if (formDataObj[key] !== undefined) {
            if (Array.isArray(formDataObj[key])) {
              formDataToSend.append(key, JSON.stringify(formDataObj[key]));
            } else {
              formDataToSend.append(key, formDataObj[key]);
            }
          }
        });
        formDataToSend.append('file', uploadFile);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vpn/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataToSend,
        });

        if (response.ok) {
          toast.success('Vpn updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['vpn', id]);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error || 'Failed to update vpn');
        }
      } else if (formData.content) {
        const formDataToSend = new FormData();
        Object.keys(formDataObj).forEach(key => {
          if (formDataObj[key] !== undefined) {
            if (Array.isArray(formDataObj[key])) {
              formDataToSend.append(key, JSON.stringify(formDataObj[key]));
            } else {
              formDataToSend.append(key, formDataObj[key]);
            }
          }
        });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vpn/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataToSend,
        });

        if (response.ok) {
          toast.success('Vpn updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['vpn', id]);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error || 'Failed to update vpn');
        }
      } else {
        // Only metadata update, no content change
        try {
          await api.put(`/vpn/${id}`, formDataObj);
          toast.success('Vpn updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['vpn', id]);
        } catch (error: any) {
          toast.error(error.response?.data?.error || 'Failed to update vpn');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update vpn');
    }
  };

  if (isLoading) {
    return <div className="loading">Loading vpn...</div>;
  }

  if (!vpn) {
    return (
      <div className="error-state">
        <p>Vpn not found</p>
        <Link to="/vpn" className="btn-primary">
          <ArrowLeft size={20} />
          <span>Back to Vpns</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="vpn-detail">
      <div className="detail-header">
        <Link to="/vpn" className="back-link">
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>
        <div className="header-actions">
          {vpn.permissions.canView && (
            <>
              <button className="btn-secondary" onClick={() => setShowShareManagement(true)}>
                <Users size={20} />
                <span>Manage Shares</span>
              </button>
              <button className="btn-secondary" onClick={handleDownload}>
                <Download size={20} />
                <span>Download</span>
              </button>
              <button className="btn-primary" onClick={() => setShowShareModal(true)}>
                <Share2 size={20} />
                <span>Share</span>
              </button>
            </>
          )}
          {vpn.permissions.canEdit && (
            <button className="btn-secondary" onClick={handleEdit}>
              <Edit size={20} />
              <span>Edit</span>
            </button>
          )}
          {vpn.permissions.canDelete && (
            <button className="btn-danger" onClick={handleDelete}>
              <Trash2 size={20} />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          <div className="detail-card">
            <div className="card-header">
              <h1>{vpn.name}</h1>
              <div className="badges">
                {vpn.serverAddress && (
                  <span className="badge">Server: {vpn.serverAddress}</span>
                )}
                {vpn.protocol && (
                  <span className="badge">Protocol: {vpn.protocol}</span>
                )}
                <span className="badge">Vpn</span>
              </div>
            </div>

            {vpn.description && (
              <div className="detail-section">
                <h3>Description</h3>
                <p>{vpn.description}</p>
              </div>
            )}

            {vpn.data && vpn.permissions.canView && (
              <div className="detail-section">
                <h3>Vpn Content</h3>
                <div className="vpn-content-wrapper">
                  <textarea
                    readOnly
                    value={vpn.data.content}
                    rows={20}
                    className="vpn-content"
                  />
                  <div className="content-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => handleCopy(vpn.data!.content, 'Vpn')}
                    >
                      <Copy size={16} />
                      <span>Copy Content</span>
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleDownload}
                    >
                      <Download size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {vpn.tags && vpn.tags.length > 0 && (
              <div className="detail-section">
                <h3>Tags</h3>
                <div className="tags">
                  {vpn.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {vpn.notes && (
              <div className="detail-section">
                <h3>Notes</h3>
                <p className="notes">{vpn.notes}</p>
              </div>
            )}

            <div className="detail-meta">
              <div className="meta-item">
                <label>Created By</label>
                <span>{vpn.createdBy.username}</span>
              </div>
              <div className="meta-item">
                <label>Created At</label>
                <span>{new Date(vpn.createdAt).toLocaleString()}</span>
              </div>
              <div className="meta-item">
                <label>Last Updated</label>
                <span>{new Date(vpn.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEditModal && editingVpn && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Vpn</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Update Content (Optional)</label>
                <input
                  type="file"
                  accept="_vpn.ovpn,.yml,.json"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setUploadFile(e.target.files[0]);
                      setFormData({ ...formData, content: '' });
                    }
                  }}
                />
                {!uploadFile && (
                  <textarea
                    placeholder="Or paste vpn content here..."
                    value={formData.content}
                    onChange={(e) => {
                      setFormData({ ...formData, content: e.target.value });
                      setUploadFile(null);
                    }}
                    rows={10}
                    style={{ marginTop: '10px', fontFamily: 'monospace' }}
                  />
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShareManagement && (
        <div className="modal-overlay" onClick={() => setShowShareManagement(false)}>
          <div className="modal-content share-management-modal" onClick={(e) => e.stopPropagation()}>
            <ShareManagement
              vpnId={vpn.id}
              resourceType="vpn"
              onClose={() => setShowShareManagement(false)}
            />
          </div>
        </div>
      )}

      {showShareModal && vpn && (
        <ShareModal
          vpnId={vpn.id}
          vpnName={vpn.name}
          resourceType="vpn"
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            setShowShareModal(false);
            queryClient.invalidateQueries(['vpn', id]);
          }}
        />
      )}
    </div>
  );
};

export default VpnDetail;

